/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { Block } from 'factories/block';
import { addScormResponseAction } from 'actions/addScormResponseAction';
import { updateAssessmentAction } from 'actions/updateAssessmentAction';
import { updateScoreAction } from 'actions/updateScoreAction';
import { AssessmentContext } from 'components/Module/AssessmentContainer';
import { HostingContext } from 'hosting/context';
import { assessmentQuestionEvent } from 'hosting/events';
import React, {
  Fragment as _,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import getBlockById from 'services/apiHelpers/getBlockById';
import getCurrentTopic from 'services/apiHelpers/getCurrentTopic';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import { Report, State } from 'store/store';
import { interactionContext as InteractionContext } from './interactionContext';
import SubmitContinue from './SubmitContinue';
import { questionXofXDefaultText } from 'components/Editor/EditAssessment/EditAssessment';
import { replaceStringVariables } from 'services/replaceStringVariables';

interface IAssessmentQuestionContext {
  singleSelect: boolean;
  selected: string[];
  isLocked: boolean;
}

const AssessmentQuestionContext = createContext<IAssessmentQuestionContext>({
  singleSelect: true,
  selected: [],
  isLocked: false,
});

const hasUpdatedScoreRaw: any = {};

const QuestionXOfX = ({ assessmentId, questionIndex, questions }) => {
  const questionXofXText = useSelector((state: State) => {
    const { props } = state?.blocks?.[assessmentId] || {};

    return props?.questionXofXtext || questionXofXDefaultText;
  });

  const questionXofXTextComped = replaceStringVariables(questionXofXText, {
    current_question: questionIndex + 1,
    total_questions: questions.length,
  });

  return questionXofXTextComped;
};

const AssessmentQuestion = ({ children, ...props }) => {
  const assessmentContext = useContext(AssessmentContext);
  const interactionContext = useContext<any>(InteractionContext);
  const hostingCtx = useContext(HostingContext);

  const {
    assessmentId,
    maxAttempts,
    questionXofXtext = questionXofXDefaultText,
  } = assessmentContext;

  const { scoring } = getBlockById(assessmentId).props as any;

  const {
    name,
    includeMedia = true,
    dividerColor = '#D1D5E7',
    questionCounterColor = 'inherit',
    font = 'inherit',
    textColor,
    correctOptions,
    //
  } = interactionContext.props;

  const { id } = props;

  // questionCounter,
  const [img, text, options, submitBtn] = children;

  // grab the labels directly with update
  // button component wouldn't otherwise render correct label ( since no rerender )
  // when editing the assessment props
  const { SUBMIT = 'Submit', CONTINUE = 'Continue' } = useSelector(
    (state: State) => {
      const { props } = state?.blocks?.[assessmentId] || {};

      const {
        assessmentQuestionSubmitLabel,
        assessmentQuestionContinueLabel,
      } = props as typeof props & {
        assessmentQuestionSubmitLabel?: string;
        assessmentQuestionContinueLabel?: string;
      };

      return {
        SUBMIT: assessmentQuestionSubmitLabel,
        CONTINUE: assessmentQuestionContinueLabel,
      };
    }
  );

  // set the default correctOptions to the first option
  if (!correctOptions.length) {
    // correctOptions = [options.props.children[0].props.id];
  }

  const assessment = useSelector((state: State) => {
    // since now we're destroying the assessment in preview on reload
    return state.assessments[assessmentId] || {};
  });

  const assessmentData = assessment && assessment.data ? assessment.data : {};

  const questionData =
    assessment && assessment.results && assessment.results[id]
      ? assessment.results[id]
      : {};

  let { submitButtonLabel = SUBMIT } = questionData || {};

  switch (submitButtonLabel) {
    case '0': {
      submitButtonLabel = CONTINUE;
      break;
    }

    case '1': {
      submitButtonLabel = SUBMIT;
      break;
    }

    case 'Continue': {
      submitButtonLabel = CONTINUE;
      break;
    }

    case 'Submit': {
      submitButtonLabel = SUBMIT;
      break;
    }
  }

  const {
    answer = [],
    isCorrect = undefined,
    attemptCount = 0,
    navigateToResults = false,
    isLocked = false,
  } = questionData;

  const selected = answer;

  const setSelected = selected => {
    dispatch(
      updateAssessmentAction({
        assessmentId,
        questionData: {
          [id]: {
            ...questionData,
            answer: selected,
          },
        },
      })
    );
  };

  const dispatch = useDispatch();
  //

  const interactionsInAssessment = useMemo(() => {
    return getInteractionsInTopic(getBlockById(assessmentId));
  }, [window.dev() ? Math.random() : assessmentId]);
  // this might well fail in dev. need to account for window.dev()
  // here in dev we ? a random value which has the effect of running each time
  // to account for adding removing questions or other settings applied
  // outside of the question

  const questions = interactionsInAssessment.filter((i: any) => {
    return [i.props.type, i.meta.subType].includes('AssessmentQuestion');
  });

  const questionIndex = questions.findIndex((q: any) => {
    //
    return q.children[0].id === id;
  });

  const singleSelect = correctOptions.length === 1;

  if (window.dev && window.dev()) {
    // isLocked = false;
  }

  const location = useMemo(() => {
    const assessment = getCurrentTopic() as Block;
    const resultsPage =
      interactionsInAssessment[interactionsInAssessment.length - 1];
    return `${assessment?.props?.sId || assessment.id}/${resultsPage?.props
      ?.sId || resultsPage.id}`;
  }, []);
  //

  const onSubmit = () => {
    if (submitButtonLabel === CONTINUE) {
      // 'Continue'
      return;
    }

    //
    const isCorrect = selected.sort().join() === correctOptions.sort().join();

    dispatch(
      updateAssessmentAction({
        assessmentId,
        questionData: {
          [id]: {
            id,
            questionName: name,
            answer: selected,
            correctAnswer: correctOptions,
            isCorrect,
            attemptCount: attemptCount ? attemptCount + 1 : 1,
            submitButtonLabel: CONTINUE,
            // 'Continue',
            navigateToResults: false,
            isLocked: true,
          },
        },
      })
    );

    let questionText: HTMLDivElement | string = document.querySelector(
      `#i${interactionContext.id} form .chameleon-text`
    ) as HTMLDivElement;

    if (questionText) {
      questionText = questionText.innerText;
    }

    //

    const optionIds = options.props.children.map((o: any) => {
      return o.props.id;
    });

    const response = selected
      .map(s => {
        return `${optionIds.indexOf(s) + 1}`;
      })
      .sort()
      .join(',');

    const getCorrectAndResponseOptions = entry => {
      let answer = '';

      //
      const content = document.querySelector(
        `#input-${entry}`
      ) as HTMLInputElement;

      if (content && content.labels) {
        answer = content.labels[0].innerText;
      }

      if (!answer) {
        console.warn('No answer found for entry:', entry);
      }

      return answer;
    };

    const correctExpanded = correctOptions
      .map(getCorrectAndResponseOptions)
      .join(',');

    const responseExpanded = selected
      .map(getCorrectAndResponseOptions)
      .join(',');

    // this mimics the multichoice / sortation pattern of updating userBehaviour
    //
    const action = {
      id,
      type: 'fill-in',
      question: questionText,
      reportable: 'multichoice' as 'multichoice',
      response: responseExpanded,
      correct: correctExpanded,
      result: !isCorrect ? 0 : 1,
    } as Report;

    hostingCtx?.data(
      assessmentQuestionEvent(interactionContext.id, {
        assessment_id: assessmentId,
        question_id: id,
        success: isCorrect,
        attemptNumber: attemptCount + 1,
        responseIds: selected,
      })
    );

    if (scoring) {
      dispatch(updateScoreAction(interactionContext.id, !isCorrect ? 0 : 1));
      dispatch(addScormResponseAction(action));
    }
  };

  useEffect(() => {
    if (!scoring || hasUpdatedScoreRaw[interactionContext.id]) {
      return;
    }

    //
    if (isLocked && isCorrect && !hasUpdatedScoreRaw[interactionContext.id]) {
      dispatch(updateScoreAction(interactionContext.id, 1));
      hasUpdatedScoreRaw[interactionContext.id] = true;
    }
  }, [isLocked, isCorrect]);

  const imgProps = img?.props?.children[1]?.props;

  const {
    width: _imgWidth_,
    height: _imgHeight_,
    imgContainerHeight,
    src,
  } = imgProps;

  const isRaster =
    src &&
    ['png', 'jpg', 'jpeg', 'gif'].includes(
      src?.slice(src?.lastIndexOf('.') + 1).toLowerCase()
    );

  const width = _imgWidth_;
  const maxHeight = `${50 + imgContainerHeight}vh`;
  const aspectRatio = `${(_imgWidth_ - imgContainerHeight * 4) / _imgHeight_}`;

  //

  return (
    <AssessmentQuestionContext.Provider
      value={
        {
          singleSelect,
          selected: window.dev && window.dev() ? correctOptions : selected,
          setSelected(id) {
            if (singleSelect) {
              setSelected([id]);

              return;
            }

            const newSelected: string[] = [...selected];

            if (!newSelected.includes(id)) {
              newSelected.push(id);
              setSelected(newSelected);
              return;
            }

            newSelected.splice(newSelected.indexOf(id), 1);
            setSelected(newSelected);
          },
          isLocked,
        } as IAssessmentQuestionContext
      }
    >
      <form
        id={`i${id}`}
        style={{
          width: '100%',
          height: '100%',
        }}
        onSubmit={e => {
          e.preventDefault();
        }}
      >
        <div
          css={css({
            padding: '50px 20px',
            // this'll lock the question
            ...(isLocked && {
              '*': {
                // pointerEvents: 'none',
                // opacity: 0.5,
              },
            }),
          })}
        >
          <h4
            // as="h4"
            css={css({
              margin: includeMedia ? 0 : '20px 0',
              fontSize: '16px',
              color: questionCounterColor || textColor,
              textAlign: 'center',
              fontFamily: `${font} !important`,

              '*': {
                fontFamily: `${font} !important`,
              },
            })}
          >
            {/* {questionXofXtextComped} */}
            <QuestionXOfX {...{ assessmentId, questionIndex, questions }} />
          </h4>

          {includeMedia && (
            <div
              css={css({
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                margin: '20px 0',
                img: {
                  //
                },
              })}
            >
              {!window.dev() && isRaster ? (
                <_>
                  <div
                    css={css({
                      width,
                      maxHeight,
                      aspectRatio,

                      img: {
                        // match the maxHeight of the container for always
                        // overriding the @media query maxHeight set more generally in the image component
                        maxHeight: `${maxHeight} !important`,
                      },
                    })}
                  >
                    {img}
                  </div>
                </_>
              ) : (
                img
              )}
            </div>
          )}

          <div>{text}</div>

          <hr
            // as="hr"
            css={css({
              margin: '30px 0 25px 0',
              border: `1px solid ${dividerColor || '#CADCF2'}`,
            })}
            aria-hidden="true"
          />
          <div
            css={css({
              ...(isLocked && {
                pointerEvents: 'none',
              }),
            })}
          >
            {options}
          </div>

          <SubmitContinue
            selected={selected}
            location={location}
            onSubmit={onSubmit}
            navigateToResults={navigateToResults}
          >
            {React.cloneElement(submitBtn, {
              label: submitButtonLabel,
            })}
          </SubmitContinue>
        </div>
      </form>
    </AssessmentQuestionContext.Provider>
  );
};

export default AssessmentQuestion;
export { AssessmentQuestionContext };
